import { supabase } from './supabaseClient';
import type { TeamMember, TaskRow } from '../types';

/**
 * Resolve o usuário do MeuGantt por trás de um e-mail de membro de equipe.
 *
 * Por que isto existe: `team_members.linked_user_id` é o ÚNICO caminho pelo qual
 * o trigger `notify_task_assigned` encontra quem avisar. O fluxo padrão do
 * módulo Equipe (digitar nome e e-mail à mão) sempre gravou `null`, então
 * atribuir tarefa a esse membro não notificava ninguém, sem erro nem aviso.
 * Só três caminhos preenchiam o campo: auto-criação do dono, bridge de
 * organização e import da organização.
 *
 * Escopo de segurança: NÃO há filtro explícito de projeto aqui porque as
 * policies de `profiles` já fazem isso melhor do que o cliente faria. As duas
 * policies relevantes são "Project members can view each other" e "Organization
 * members can view each other": quem não divide projeto nem organização comigo
 * simplesmente não retorna. Ou seja, ninguém consegue usar isto para descobrir
 * se um e-mail qualquer tem conta, e ninguém é ligado a um projeto que não
 * poderia abrir.
 */
async function resolveLinkedUserId(email?: string | null): Promise<string | null> {
  const normalized = email?.trim();
  if (!normalized) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('id')
    .ilike('email', normalized)
    .limit(1);

  // Falha de resolução não pode impedir a criação do membro: o membro sem
  // usuário ligado continua sendo útil para alocação e capacidade, só não
  // recebe notificação.
  if (error) {
    console.error('[teamService] falha ao resolver linked_user_id:', error);
    return null;
  }
  return data?.[0]?.id ?? null;
}

export const teamService = {
  resolveLinkedUserId,

  // Buscar membros de um projeto
  async getByProject(projectId: string): Promise<TeamMember[]> {
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .eq('project_id', projectId)
      .order('name', { ascending: true });

    if (error) {
      // Ignorar AbortError - causado pelo React StrictMode
      if (error.message?.includes('AbortError')) {
        console.log('[teamService] getByProject AbortError ignorado');
        return [];
      }
      throw error;
    }
    return data as TeamMember[];
  },

  // Criar membro
  async create(member: Partial<TeamMember> & { project_id: string; name: string; color?: string }): Promise<TeamMember> {
    const linkedUserId = member.linked_user_id || (await resolveLinkedUserId(member.email));

    const { data, error } = await supabase
      .from('team_members')
      .insert([{
        project_id: member.project_id,
        name: member.name,
        email: member.email || null,
        role_title: member.role_title || null,
        avatar_color: member.avatar_color || member.color || '#7e3eee',
        resource_id: member.resource_id || null,
        linked_user_id: linkedUserId
      }])
      .select()
      .single();

    if (error) throw error;
    return data as TeamMember;
  },

  // Criar múltiplos membros
  async createMany(members: (Partial<TeamMember> & { project_id: string; name: string; color?: string })[]): Promise<TeamMember[]> {
    // Uma resolução por e-mail distinto, não uma por membro: import de planilha
    // costuma repetir a mesma pessoa em projetos diferentes.
    const emails = [...new Set(
      members.filter(m => !m.linked_user_id && m.email?.trim()).map(m => m.email!.trim().toLowerCase())
    )];
    const resolved = new Map<string, string | null>();
    for (const email of emails) resolved.set(email, await resolveLinkedUserId(email));

    const { data, error } = await supabase
      .from('team_members')
      .insert(members.map(m => ({
        project_id: m.project_id,
        name: m.name,
        email: m.email || null,
        role_title: m.role_title || null,
        avatar_color: m.avatar_color || m.color || '#7e3eee',
        resource_id: m.resource_id || null,
        linked_user_id: m.linked_user_id
          || (m.email?.trim() ? resolved.get(m.email.trim().toLowerCase()) ?? null : null)
      })))
      .select();

    if (error) throw error;
    return data as TeamMember[];
  },

  // Atualizar membro
  async update(id: string, updates: Partial<TeamMember>): Promise<TeamMember> {
    // Corrigir o e-mail de um membro é o momento natural de religar a pessoa.
    // Sem isto, quem digitou o e-mail errado na criação ficava sem notificação
    // para sempre, mesmo depois de consertar o cadastro.
    const patch: Partial<TeamMember> = { ...updates };
    if (patch.email !== undefined && patch.linked_user_id === undefined) {
      patch.linked_user_id = await resolveLinkedUserId(patch.email);
    }

    const { data, error } = await supabase
      .from('team_members')
      .update(patch)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as TeamMember;
  },

  // Buscar membro por resource_id em um projeto
  async getByProjectAndResource(projectId: string, resourceId: string): Promise<TeamMember | null> {
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .eq('project_id', projectId)
      .eq('resource_id', resourceId)
      .maybeSingle();

    if (error) throw error;
    return data as TeamMember | null;
  },

  // Deletar membro
  async delete(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  // Buscar IDs de team_members linkados ao usuário
  async getMyMemberIds(userId: string): Promise<{ id: string; project_id: string }[]> {
    const { data, error } = await supabase
      .from('team_members')
      .select('id, project_id')
      .eq('linked_user_id', userId);

    if (error) throw error;
    return data || [];
  },

  // Buscar todas as tarefas do usuário logado em todos os projetos
  async getMyTasksAcrossProjects(userId: string): Promise<(TaskRow & { project_name: string })[]> {
    // 1. Buscar team_members linkados ao usuário
    const { data: teamMembers, error: tmError } = await supabase
      .from('team_members')
      .select('id, project_id')
      .eq('linked_user_id', userId);

    if (tmError) throw tmError;
    if (!teamMembers || teamMembers.length === 0) return [];

    const memberIds = teamMembers.map(tm => tm.id);

    // 2. Buscar tarefas atribuídas a esses team_members
    const { data: tasks, error: taskError } = await supabase
      .from('tasks')
      .select('*, projects(name)')
      .in('assigned', memberIds)
      .neq('type', 'group')
      .order('end_date', { ascending: true, nullsFirst: false });

    if (taskError) throw taskError;

    return (tasks || []).map((t: unknown) => {
      const row = t as TaskRow & { projects?: { name: string } | null };
      return {
        ...row,
        project_name: row.projects?.name || ''
      };
    });
  }
};
